import { useMutation } from '@apollo/client';
import { UsageTrackerEvent, usageTracker } from '@volumental/analytic';
import surveyBannerImg from '@volumental/assets/images/Hybrid/surveyBanner.png';
import { BannerImage, Container, Content } from '@volumental/common';
import { type JSX, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { submitPreviousPurchaseMutation } from '../../api/mutations/submit-previous-purchase-mutation';
import { Header } from '../../components/accessible-components';
import { trackIfUserDroppedOffSite } from '../../services/window-service';
import { useInputStore, useSurveyStore } from '../../store';
import { type ResultStep, SurveyStep } from '../../store/survey';
import { type FitSurveyResult } from '../../types/types';
import { FooterButtons } from './components/footer-buttons';
import { RemoveModal } from './components/remove-modal';
import { ResultBox } from './components/result-box';
import { SurveyTitle } from './components/survey-title';
import { SelectorModal } from './selectors/selector-modal';

interface SurveyProps {
  onClose?: () => void;
  onSkip?: () => void;
  onBack?: () => void;
  onContinue: () => void;
  submitButtonText?: string;
}

export function FitSurvey({
  onBack,
  onClose,
  onContinue,
  onSkip,
  submitButtonText,
}: SurveyProps): JSX.Element {
  const { t } = useTranslation();
  const { setPreviousPurchaseID } = useInputStore((store) => store);
  const { survey, onNext, onEdit, clearSurveyStore } = useSurveyStore(
    (store) => store,
  );

  const [surveyToRemove, setSurveyToRemove] = useState<
    ResultStep | undefined
  >();
  const [submitPreviousPurchase, { loading }] = useMutation(
    submitPreviousPurchaseMutation,
  );

  useEffect(() => {
    trackIfUserDroppedOffSite();
  }, []);

  const onSubmit = (): void => {
    if (survey.step === SurveyStep.Result) {
      const { brand, gender, length, locale, width } = survey;
      usageTracker.track(UsageTrackerEvent.SubmitSurvey);
      const result: FitSurveyResult = {
        gender_age: gender.value,
        brand_name: brand.value,
        width: width.value,
        length: length.value,
        size_locale: locale,
        widthLabel: width.label,
        lengthLabel: length.label,
        brandLabel: brand.label,
      };
      void submitPreviousPurchase({
        variables: { gbswl: result },
      }).then((res) => {
        const id = res.data?.registerPreviousPurchase?.id;
        if (!id) {
          return;
        }
        setPreviousPurchaseID(id);
        onContinue();
      });
    }
  };

  return (
    <Container>
      <Header close={onClose} back={onBack} />
      <Content disableScrollbar>
        <SurveyTitle showDescription={survey.step !== SurveyStep.Result} />
        {survey.step === SurveyStep.Result ? (
          <ResultBox
            survey={survey}
            onEdit={onEdit}
            onRemove={() => {
              setSurveyToRemove(survey);
            }}
          />
        ) : (
          <BannerImage
            src={surveyBannerImg}
            alt={t('imageAlt.onboardingSurvey')}
            style={{ marginTop: '24px' }}
          />
        )}
      </Content>
      <FooterButtons
        onClick={
          survey.step === SurveyStep.Result
            ? onSubmit
            : () => {
                onNext({});
              }
        }
        onSkip={survey.step === SurveyStep.Result ? undefined : onSkip}
        loading={loading}
        buttonText={
          survey.step === SurveyStep.Result
            ? submitButtonText
            : t('button.addShoes')
        }
      />
      <SelectorModal />
      <RemoveModal
        survey={surveyToRemove}
        onClose={() => {
          setSurveyToRemove(undefined);
        }}
        onRemove={() => {
          clearSurveyStore();
          setSurveyToRemove(undefined);
        }}
      />
    </Container>
  );
}
